import axios from 'axios';
import apiClient from '@/api/apiClient';
import type {
  AdminUser,
  AdminUserPage,
  AdminUserQuery,
  CreateUserInput,
  UpdateUserInput,
  UserStatistics,
} from '../types';

interface ApiResponse<T> {
  code: number | string;
  message: string;
  result: T;
}

interface ApiErrorBody {
  code?: number | string;
  message?: string;
  result?: Record<string, string[]>;
}

export class AdminUsersApiError extends Error {
  code?: number | string;
  status?: number;
  fieldErrors?: Record<string, string[]>;

  constructor(
    message: string,
    options: { code?: number | string; status?: number; fieldErrors?: Record<string, string[]> } = {},
  ) {
    super(message);
    this.name = 'AdminUsersApiError';
    this.code = options.code;
    this.status = options.status;
    this.fieldErrors = options.fieldErrors;
  }
}

export const toAdminUsersApiError = (error: unknown): AdminUsersApiError => {
  if (error instanceof AdminUsersApiError) return error;
  if (axios.isAxiosError<ApiErrorBody>(error)) {
    return new AdminUsersApiError(
      error.response?.data?.message
        || (error.code === 'ECONNABORTED'
          ? 'Yêu cầu đã quá thời gian chờ. Vui lòng thử lại.'
          : error.response
            ? 'Máy chủ không thể thực hiện yêu cầu.'
            : 'Không thể kết nối đến máy chủ.'),
      {
        code: error.response?.data?.code,
        status: error.response?.status,
        fieldErrors: error.response?.data?.result,
      },
    );
  }
  return new AdminUsersApiError(
    error instanceof Error ? error.message : 'Đã xảy ra lỗi không xác định.',
  );
};

const withNormalizedError = async <T>(request: () => Promise<T>): Promise<T> => {
  try {
    return await request();
  } catch (error: unknown) {
    throw toAdminUsersApiError(error);
  }
};

export const getAdminUsers = (query: AdminUserQuery): Promise<AdminUserPage> =>
  withNormalizedError(async () => {
    const response = await apiClient.get<ApiResponse<AdminUserPage>>('/api/users', {
      params: {
        ...query,
        search: query.search || undefined,
        role: query.role || undefined,
        status: query.status || undefined,
      },
    });
    return response.data.result;
  });

export const getUserStatistics = (): Promise<UserStatistics> =>
  withNormalizedError(async () => {
    const response = await apiClient.get<ApiResponse<UserStatistics>>('/api/users/statistics');
    return response.data.result;
  });

export const getAdminUser = (userId: number): Promise<AdminUser> =>
  withNormalizedError(async () => {
    const response = await apiClient.get<ApiResponse<AdminUser>>(`/api/users/${userId}`);
    return response.data.result;
  });

export const createAdminUser = (input: CreateUserInput): Promise<AdminUser> =>
  withNormalizedError(async () => {
    const response = await apiClient.post<ApiResponse<AdminUser>>('/api/users', input);
    return response.data.result;
  });

export const updateAdminUser = (userId: number, input: UpdateUserInput): Promise<AdminUser> =>
  withNormalizedError(async () => {
    const response = await apiClient.put<ApiResponse<AdminUser>>(`/api/users/${userId}`, input);
    return response.data.result;
  });

export const updateAdminUserStatus = (userId: number, active: boolean): Promise<AdminUser> =>
  withNormalizedError(async () => {
    const response = await apiClient.patch<ApiResponse<AdminUser>>(`/api/users/${userId}/status`, {
      active,
    });
    return response.data.result;
  });
